import type { BaseClient } from '../../http/BaseClient.js';
import type {
  CreateDashboardRequest,
  CreateSnapshotShareRequest,
  CreateSnapshotShareResponse,
  DashboardResponse,
  ExportAuthorization,
  ExportRequest,
  ListDashboardsResponse,
  ListDashboardVersionsResponse,
  ListSnapshotsResponse,
  ListSnapshotSharesResponse,
  OkResponse,
  PopulateDashboardParams,
  PopulateDashboardResult,
  SnapshotResponse,
  UpdateDashboardRequest,
} from './api-types.js';

/**
 * DashboardsClient is the visual-dashboard control plane (Talk-to-Your-BI block
 * B5): save/load/edit the durable Dashboard Spec, refresh it with current data
 * (run-as-owner, model-free), and freeze/share snapshots.
 *
 * Access is org-scoped + product-entitlement-gated (requireProduct("bi")) +
 * project-membership-scoped server-side. Dynamic boards are same-org, member-only;
 * static snapshots are the externally-shareable, governed-PII-egress artifact.
 */
export class DashboardsClient {
  constructor(
    private readonly http: BaseClient,
    private readonly baseUrl: string,
  ) {}

  private base(workspaceId: string): string {
    return `/workspaces/${workspaceId}/bi/dashboards`;
  }

  /** Lists a project's saved dashboards (most-recently-updated first). */
  list(workspaceId: string): Promise<ListDashboardsResponse> {
    return this.http.get<ListDashboardsResponse>(this.base(workspaceId));
  }

  /** Fetches one dashboard (spec served verbatim; validate client-side). */
  get(workspaceId: string, dashboardId: string): Promise<DashboardResponse> {
    return this.http.get<DashboardResponse>(`${this.base(workspaceId)}/${dashboardId}`);
  }

  /** Saves a spec as a new dashboard (the spec is a validated DashboardSpec). */
  create(workspaceId: string, body: CreateDashboardRequest): Promise<DashboardResponse> {
    return this.http.post<DashboardResponse>(this.base(workspaceId), { body });
  }

  /**
   * Updates a dashboard. A new `spec` bumps specVersion and appends an immutable
   * version row; name/description-only edits do not bump.
   */
  update(workspaceId: string, dashboardId: string, body: UpdateDashboardRequest): Promise<DashboardResponse> {
    return this.http.patch<DashboardResponse>(`${this.base(workspaceId)}/${dashboardId}`, { body });
  }

  /** Soft-deletes a dashboard (owner or project ADMIN). */
  remove(workspaceId: string, dashboardId: string): Promise<OkResponse> {
    return this.http.delete<OkResponse>(`${this.base(workspaceId)}/${dashboardId}`);
  }

  /** Lists a dashboard's spec history (newest first). */
  listVersions(workspaceId: string, dashboardId: string): Promise<ListDashboardVersionsResponse> {
    return this.http.get<ListDashboardVersionsResponse>(`${this.base(workspaceId)}/${dashboardId}/versions`);
  }

  /** Restores a prior version by forward-writing it as a new version. */
  restoreVersion(workspaceId: string, dashboardId: string, specVersion: number): Promise<DashboardResponse> {
    return this.http.post<DashboardResponse>(`${this.base(workspaceId)}/${dashboardId}/versions/${specVersion}/restore`, {});
  }

  /**
   * Refreshes the dashboard with CURRENT data — the model-free, run-as-owner
   * populate. Returns one PanelPayload per panel. `params` are resolved control
   * values (interactivity rungs 2–3).
   */
  populate(workspaceId: string, dashboardId: string, body: PopulateDashboardParams = {}): Promise<PopulateDashboardResult> {
    return this.http.post<PopulateDashboardResult>(`${this.base(workspaceId)}/${dashboardId}/populate`, { body });
  }

  /**
   * Freezes a snapshot — the governed PII egress (explicit action + audit). The
   * frozen panelData carries re-hydrated names.
   */
  createSnapshot(workspaceId: string, dashboardId: string, body: PopulateDashboardParams = {}): Promise<SnapshotResponse> {
    return this.http.post<SnapshotResponse>(`${this.base(workspaceId)}/${dashboardId}/snapshots`, { body });
  }

  /** Lists a dashboard's snapshots (newest first). */
  listSnapshots(workspaceId: string, dashboardId: string): Promise<ListSnapshotsResponse> {
    return this.http.get<ListSnapshotsResponse>(`${this.base(workspaceId)}/${dashboardId}/snapshots`);
  }

  /** Reads one snapshot (same-org; no live query, no credential). */
  getSnapshot(workspaceId: string, dashboardId: string, snapshotId: string): Promise<SnapshotResponse> {
    return this.http.get<SnapshotResponse>(`${this.base(workspaceId)}/${dashboardId}/snapshots/${snapshotId}`);
  }

  /**
   * Mints a snapshot share — an internal/known-user grant or an anonymous link.
   * External sharing (a link or a cross-org user) requires the org's
   * allowExternalSharing flag. A minted link's raw secret is returned ONCE in
   * `linkToken`; only its hash is stored.
   */
  createSnapshotShare(
    workspaceId: string,
    dashboardId: string,
    snapshotId: string,
    body: CreateSnapshotShareRequest,
  ): Promise<CreateSnapshotShareResponse> {
    return this.http.post<CreateSnapshotShareResponse>(
      `${this.base(workspaceId)}/${dashboardId}/snapshots/${snapshotId}/shares`,
      { body },
    );
  }

  /** Lists a snapshot's shares (owner/ADMIN; link secrets never returned). */
  listSnapshotShares(workspaceId: string, dashboardId: string, snapshotId: string): Promise<ListSnapshotSharesResponse> {
    return this.http.get<ListSnapshotSharesResponse>(
      `${this.base(workspaceId)}/${dashboardId}/snapshots/${snapshotId}/shares`,
    );
  }

  /** Revokes a share/link. */
  revokeSnapshotShare(workspaceId: string, dashboardId: string, snapshotId: string, shareId: string): Promise<OkResponse> {
    return this.http.delete<OkResponse>(
      `${this.base(workspaceId)}/${dashboardId}/snapshots/${snapshotId}/shares/${shareId}`,
    );
  }

  /**
   * Reads a snapshot shared with the caller via an explicit user grant (internal
   * or cross-org). Not project-scoped — the grant is the authorization.
   */
  getSharedSnapshot(snapshotId: string): Promise<SnapshotResponse> {
    return this.http.get<SnapshotResponse>(`/bi/snapshots/${snapshotId}/shared`);
  }

  /**
   * Reads a snapshot via an anonymous link token (no session). Use `token` = the
   * raw secret returned by createSnapshotShare({ link: true }).
   */
  getPublicSnapshot(token: string): Promise<SnapshotResponse> {
    return this.http.get<SnapshotResponse>(`/public/bi/snapshots/${encodeURIComponent(token)}`);
  }

  /** The shareable URL for an anonymous link token (for copy-to-clipboard). */
  publicSnapshotUrl(token: string): string {
    return `${this.baseUrl}/public/bi/snapshots/${encodeURIComponent(token)}`;
  }

  /**
   * Authorizes + audits a client-side export BEFORE the caller serialises the file
   * (Talk-to-Your-BI block B7). This makes the O1 "audit exports always" guarantee
   * un-skippable: the file must not be produced until this resolves. A workspace-
   * scoped export is same-org (project membership), so it is audit-only; the
   * allowExternalSharing gate governs only the cross-org / anonymous-link recipient
   * surfaces. Throws HttpError(403) when a gated egress is disallowed.
   */
  recordExport(workspaceId: string, dashboardId: string, body: ExportRequest): Promise<ExportAuthorization> {
    return this.http.post<ExportAuthorization>(`${this.base(workspaceId)}/${dashboardId}/exports`, { body });
  }
}
